import React from 'react';
import {
  Grid,
  GridNoRecords,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent
} from '@progress/kendo-react-grid';
import { useParams } from 'react-router-dom';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';

import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../../components/grid/grid-tool-bar';
import { ColumnMenu } from '../../../../components/columnMenu';
import { NoRecords } from '../../../../components/grid/no-records';
import { DefaultGridSettings } from '../../../../constants/grid-settings';
import { useGetEventReportResponseFeedback } from '../../../../hooks/services/use-reports';

const initialDataState: State = {
  sort: [{ field: 'id', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const urlRegex = /^(https?:\/\/)([\w-]+(\.[\w-]+)+)([\/?#][^\s]*)?$/i;

const isUrl = (value: string): boolean => {
  return typeof value === 'string' && urlRegex.test(value);
};

const getFileExtension = (url: string): string => {
  try {
    const pathname = decodeURIComponent(new URL(url).pathname);
    const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
    return match ? match[1].toLowerCase() : '';
  } catch {
    return '';
  }
};

const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'webp',
  'svg',
  'tiff',
  'ico'
];

const VIDEO_EXTENSIONS = [
  'mp4',
  'webm',
  'ogg',
  'mov',
  'avi',
  'mkv',
  'm4v',
  '3gp'
];

type MediaType = 'image' | 'video' | 'other';

const getMediaType = (url: string): MediaType => {
  const ext = getFileExtension(url);
  if (IMAGE_EXTENSIONS.includes(ext)) return 'image';
  if (VIDEO_EXTENSIONS.includes(ext)) return 'video';
  return 'other';
};

export const FeedbackResponsesTab: React.FC = () => {
  const { eventId, detailId } = useParams();

  const [dataState, setDataState] = React.useState<State>(initialDataState);
  const [previewUrl, setPreviewUrl] = React.useState<string>('');

  const { data, isLoading } = useGetEventReportResponseFeedback({
    eventId,
    eventFeedbackId: detailId,
    params: `${toDataSourceRequestString(dataState)}`
  });

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const answerColumn = (props: GridCellProps) => {
    const answer = props.dataItem.answer;

    if (isUrl(answer))
      return (
        <td className={'k-table-td'}>
          <Button themeColor={'primary'} onClick={() => setPreviewUrl(answer)}>
            Preview
          </Button>
        </td>
      );
    return <td className={'k-table-td'}>{answer}</td>;
  };

  const renderPreviewContent = () => {
    const mediaType = getMediaType(previewUrl);

    if (mediaType === 'image') {
      return (
        <img
          src={previewUrl}
          alt={'Preview'}
          style={{
            maxWidth: '100%',
            maxHeight: '70vh',
            display: 'block',
            margin: '0 auto'
          }}
        />
      );
    }

    if (mediaType === 'video') {
      return (
        <video
          src={previewUrl}
          controls
          style={{
            maxWidth: '100%',
            maxHeight: '70vh',
            display: 'block',
            margin: '0 auto'
          }}
        />
      );
    }

    return (
      <div
        className={
          'd-flex flex-column align-items-center gap-3 text-muted py-4'
        }>
        <span>Preview not available for this file</span>
        <Button
          themeColor={'primary'}
          onClick={() =>
            window.open(previewUrl, '_blank', 'noopener,noreferrer')
          }>
          Download
        </Button>
      </div>
    );
  };

  return (
    <>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search responses'}
          columnsToSearch={['respondent', 'question', 'answer']}
          exportRef={gridExportRef}
          showGridMode={false}
          showCardMode={false}
          showBulkExport={true}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
        />
        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={`task.xlsx`}>
          <Grid
            id={'grid-responses'}
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            data={data}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column
              field={'respondent'}
              title={'Respondent'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'question'}
              title={'Question'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'answer'}
              title={'Answer'}
              columnMenu={ColumnMenu}
              cells={{ data: (props) => answerColumn(props) }}
            />

            <GridNoRecords>
              {isLoading ? (
                <div
                  className={
                    'd-flex flex-column align-items-center gap-2 text-muted'
                  }>
                  <Loader type={'pulsing'} />
                  <span>Loading data</span>
                </div>
              ) : (
                <NoRecords />
              )}
            </GridNoRecords>
          </Grid>
        </ExcelExport>
      </div>

      {previewUrl && (
        <Dialog
          closeIcon
          title={'Media Preview'}
          onClose={() => setPreviewUrl('')}
          width={720}>
          {renderPreviewContent()}
        </Dialog>
      )}
    </>
  );
};
